/**
 * Delivery Selector Component
 *
 * Multi-step delivery date, time slot, and options selector.
 * Follows product-form.js pattern for cart integration.
 *
 * Modes (set by prefillFromCart after reading cart.attributes):
 *   FRESH    - Cart has no delivery date. Full 4-step flow.
 *   CONFIRM  - Cart has a valid delivery date for this product. Skip date/time steps.
 *   CONFLICT - Cart date exists but is too early for this product's min_preorder_days.
 *              Forces new date selection; updating updates the whole order.
 *
 * Order-level data stored in cart.attributes (not line item properties):
 *   "Delivery Date", "Delivery Time", "Phone Number"
 *
 * Item-level data kept as line item properties:
 *   "Message on Card", "Birthday Partyware"
 *
 * Timezone: All cutoff calculations use Malaysia Time (MYT = UTC+8, no DST),
 * regardless of the customer's browser/device timezone.
 */

/**
 * Phone-number rule on the product page.
 *   true  → phone is required for every order (current behaviour).
 *   false → phone is required only for Self-Pickup and Postage. For Delivery
 *           it's optional on the PDP because Shopify checkout already collects
 *           (and requires) the shipping-address phone — see order data: every
 *           Delivery order has shippingAddress.phone; Self-Pickup orders have
 *           NO other phone, so they keep the requirement.
 * Flip to false only after Sam confirms ops (OFD WhatsApp) can read the
 * checkout phone for Delivery orders.
 */
const YS_PHONE_REQUIRED_FOR_DELIVERY = true;

if (!customElements.get("delivery-selector-form")) {
  customElements.define(
    "delivery-selector-form",
    class DeliverySelectorForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.buyNowButton = this.querySelector("[data-buy-now]");

        // Delivery selector state
        this.selectedDate = null;
        this.selectedTime = null;
        this.selectedVariantId = null;
        this.selectedVariantPrice = null;
        this.selectedVariantTitle = null;
        this.partywareChoice = "No";
        this.fulfillmentMethod = "Delivery"; // Delivery | Self-Pickup
        this.deliveryType = "Normal";        // Normal | Door-to-Door (delivery only)
        this.fulfillmentOutlet = "";         // outlet name when Self-Pickup
        this.postageBatch = "";              // "Batch 1 (ships 7–10 Sept)" when Postage
        this.postageAck = false;             // acknowledgement ticked
        this.fulfillmentPrefilled = false;   // true when cart attrs already carry a choice
        this.currentStep = 1;
        this.config = null;
        this.error = false;
        this.mode = "FRESH"; // FRESH | CONFIRM | CONFLICT

        // Price update state
        this.currentPrice = null;
        this.currencyFormat = null;

        // Set to true when user clicks "Edit selections" so Date/Time don't auto-skip
        this.hasUserGoneBack = false;

        // Order-sheet layout (data-layout="sheet"): every group stays visible;
        // advanceToStep() only moves the highlight. See docs/superpowers/specs/
        // 2026-08-20-order-sheet-design.md
        this.sheet = this.dataset.layout === "sheet";
        this.sheetReady = false; // no auto-scrolling until initial render is done
      }

      connectedCallback() {
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        if (this.buyNowButton) {
          this.buyNowButton.addEventListener(
            "click",
            this.onBuyNowHandler.bind(this)
          );
        }

        this.loadProductConfig();
        this.setupStepNavigation();
        this.setupFulfillmentChoice();
        this.advanceToStep(this.currentStep);

        this.prefillFromCart().then((prefillStatus) => {
          this.setupDatePicker();
          this.setupTimeSlots();
          this.applyPrefilledTimeSlot();
          this.setupOptions();
          this.setupFinalDetails();
          this.applyPrefilledPhone(prefillStatus?.phone);
          this.setupStickyAddToCartScroll();
          this.setupPriceUpdate();
          this.updateStepIndicatorText();
          this.setupSheetFocus();
          this.refreshStickyCta();
          this.sheetReady = true;
        });
      }

      /** Sheet mode: focusing the details fields moves the highlight to group 5. */
      setupSheetFocus() {
        if (!this.sheet) return;
        this.form.querySelectorAll('[name="phone"], [name="message"]').forEach((el) => {
          el.addEventListener("focus", () => {
            if (this.currentStep !== 5) this.advanceToStep(5, { skipPrefillAdvance: true, noScroll: true });
          });
        });
      }

      /** Sheet mode: which groups are answered. */
      refreshSheetDone() {
        if (!this.sheet) return;
        const phone = (this.form.querySelector('[name="phone"]')?.value || "").trim();
        const phoneOk = this.isPhoneRequired() ? phone.length >= 10 : phone.length === 0 || phone.length >= 10;
        const m = this.fulfillmentMethod;
        const receiveOk = m === "Self-Pickup" ? !!this.fulfillmentOutlet : m === "Postage" ? !!(this.postageBatch && this.postageAck) : true;
        const done = { 1: !!this.selectedVariantId, 2: !!this.selectedDate, 3: !!this.selectedTime, 4: receiveOk, 5: phoneOk && phone.length > 0 || (phoneOk && !this.isPhoneRequired()) };
        this.querySelectorAll("[data-step-content]").forEach((c) => {
          c.classList.toggle("is-done", !!done[parseInt(c.dataset.stepContent)]);
        });
      }

      /** Sheet mode: the first group still missing an answer → {step, label}. */
      firstMissing() {
        if (!this.selectedVariantId) return { step: 1, label: "Choose size" };
        if (this.fulfillmentMethod === "Postage") {
          if (!this.postageBatch) return { step: 4, label: "Choose batch" };
          if (!this.postageAck) return { step: 4, label: "Confirm postage" };
        } else {
          if (!this.selectedDate) return { step: 2, label: "Pick a date" };
          if (!this.selectedTime) return { step: 3, label: "Pick a time" };
          if (this.fulfillmentMethod === "Self-Pickup" && !this.fulfillmentOutlet) return { step: 4, label: "Choose outlet" };
        }
        const phone = (this.form.querySelector('[name="phone"]')?.value || "").trim();
        if (this.isPhoneRequired() && phone.length < 10) return { step: 5, label: "Enter phone" };
        if (phone.length > 0 && phone.length < 10) return { step: 5, label: "Check phone" };
        return null;
      }

      scrollToGroup(step, opts = {}) {
        const el = this.querySelector(`[data-step-content="${step}"]`) || this;
        el.scrollIntoView({ behavior: "smooth", block: opts.block || "start" });
      }

      /**
       * Step 4 — "How would you like to receive your order?"
       * Method cards (Delivery / Self-Pickup), delivery sub-options
       * (Normal / Door-to-Door +RM5), pickup outlet cards, and the
       * postcode delivery-fee checker. Prefills from cart attributes so a
       * returning customer (or 2nd product) skips straight through.
       */
      setupFulfillmentChoice() {
        const methodCards = this.querySelectorAll("[data-receive-method]");
        if (!methodCards.length) return;
        const typeCards = this.querySelectorAll("[data-delivery-type]");
        const outletCards = this.querySelectorAll("[data-pickup-outlet]");
        const panels = this.querySelectorAll("[data-receive-panel]");
        const continueBtn = this.querySelector("[data-receive-continue]");
        const pickupHint = this.querySelector("[data-pickup-hint]");

        const selectMethod = (card) => {
          methodCards.forEach((c) => c.classList.toggle("active", c === card));
          this.fulfillmentMethod = card.dataset.method;
          panels.forEach((p) => {
            p.style.display = p.dataset.receivePanel === this.fulfillmentMethod ? "" : "none";
          });
          if (this.fulfillmentMethod === "Delivery") this.fulfillmentOutlet = "";
          if (this.fulfillmentMethod !== "Postage") { this.postageBatch = ""; }
          this.updateStepIndicatorText();
          if (this.sheet) this.syncPhoneRequirement(); // phone rule can differ per method
          this.refreshStickyCta();
        };
        // Postage: batch cards + acknowledgement
        const batchCards = this.querySelectorAll("[data-postage-batch]");
        const batchHint = this.querySelector("[data-postage-batch-hint]");
        const ackBox = this.querySelector("[data-postage-ack]");
        const ackHint = this.querySelector("[data-postage-ack-hint]");
        batchCards.forEach((c) => c.addEventListener("click", () => {
          batchCards.forEach((x) => x.classList.toggle("active", x === c));
          this.postageBatch = c.dataset.batch;
          if (batchHint) batchHint.style.display = "none";
          this.refreshStickyCta();
        }));
        if (ackBox) ackBox.addEventListener("change", () => {
          this.postageAck = ackBox.checked;
          if (ackHint && ackBox.checked) ackHint.style.display = "none";
          this.refreshStickyCta();
        });
        const selectType = (card) => {
          typeCards.forEach((c) => c.classList.toggle("active", c === card));
          this.deliveryType = card.dataset.type; // Normal | Door-to-Door
        };
        const selectOutlet = (card) => {
          outletCards.forEach((c) => c.classList.toggle("active", c === card));
          this.fulfillmentOutlet = card.dataset.outlet;
          if (pickupHint) pickupHint.style.display = "none";
          this.refreshStickyCta();
        };

        methodCards.forEach((c) => c.addEventListener("click", () => selectMethod(c)));
        typeCards.forEach((c) => c.addEventListener("click", () => selectType(c)));
        outletCards.forEach((c) => c.addEventListener("click", () => selectOutlet(c)));

        if (continueBtn) {
          continueBtn.addEventListener("click", () => {
            if (this.fulfillmentMethod === "Self-Pickup" && !this.fulfillmentOutlet) {
              if (pickupHint) pickupHint.style.display = "";
              return;
            }
            if (this.fulfillmentMethod === "Postage") {
              if (!this.postageBatch) { if (batchHint) batchHint.style.display = ""; return; }
              if (!this.postageAck) { if (ackHint) ackHint.style.display = ""; return; }
              // Mixed-cart guard: postage is for this product only.
              this.checkPostageCartConflict().then((conflict) => {
                if (conflict) return;
                this.advanceToStep(5);
                this.updateSummary();
              });
              return;
            }
            // Receive-first flow: go to Date next; classic flow: to details.
            if (this.config?.receiveFirst && !this.selectedDate) {
              this.advanceToStep(2);
            } else {
              this.advanceToStep(5);
            }
            this.updateSummary();
          });
        }

        // Postcode delivery-fee checker (zone map embedded by the snippet).
        const feeInput = this.querySelector("[data-fee-postcode]");
        const feeBtn = this.querySelector("[data-fee-check]");
        const feeResult = this.querySelector("[data-fee-result]");
        const feeMapEl = this.querySelector("[data-ys-zone-fees]");
        if (feeBtn && feeInput && feeResult && feeMapEl) {
          let feeByPostcode = null;
          try {
            const zones = JSON.parse(feeMapEl.textContent);
            feeByPostcode = {};
            Object.keys(zones).forEach((fee) => {
              zones[fee].forEach((pc) => (feeByPostcode[pc] = fee));
            });
          } catch (e) {
            feeByPostcode = null;
          }
          const check = () => {
            if (!feeByPostcode) return;
            const pc = (feeInput.value || "").replace(/\D/g, "");
            feeResult.style.display = "";
            feeResult.classList.remove("ys-fee--unavailable");
            if (pc.length !== 5) {
              feeResult.classList.add("ys-fee--unavailable");
              feeResult.textContent = "Please enter a 5-digit postcode.";
              return;
            }
            const fee = feeByPostcode[pc];
            if (fee) {
              feeResult.textContent = `🛵 Estimated delivery fee to ${pc}: RM${fee}.00`;
            } else {
              feeResult.classList.add("ys-fee--unavailable");
              feeResult.textContent =
                "Hmm, we may not cover this area yet — chat with us on WhatsApp and we'll try to arrange it! 💬";
            }
          };
          feeBtn.addEventListener("click", check);
          feeInput.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              check();
            }
          });
        }

        // Prefill from cart attributes (customer already chose this order).
        fetch("/cart.js")
          .then((r) => r.json())
          .then((cart) => {
            let method = cart?.attributes?.["Fulfillment Method"];
            let cartBatch = cart?.attributes?.["Postage Batch"] || "";
            const stillHasCrisps = (cart?.items || []).some((it) => /egg yolk chicken floss mochi cri/i.test(it.product_title || ""));
            if (method === "Postage" && !stillHasCrisps) {
              // Stale postage flag (crisps were removed) — treat as a fresh order.
              method = ""; cartBatch = "";
            }
            if (method === "Postage" && cartBatch) {
              this.postageBatch = cartBatch;
              const bc = [...this.querySelectorAll("[data-postage-batch]")].find((b) => b.dataset.batch === cartBatch);
              if (bc) bc.classList.add("active");
            }
            const outlet = cart?.attributes?.["Pickup Outlet"] || "";
            const dtype = /door/i.test(cart?.attributes?.["Delivery Type"] || "") ? "Door-to-Door" : "Normal";
            if (!method) return;
            const mCard = Array.from(methodCards).find((c) => c.dataset.method === method);
            if (!mCard) {
              // Cart is already a Postage order (crisps) and this product can't
              // be posted. Postage never mixes with Delivery/Pickup, so we do
              // NOT let this page silently flip the order to Delivery. The
              // customer may still add the item; the cart then keeps its
              // Postage attributes and the cart banner blocks checkout, naming
              // the items to remove. Flow here: Size → notice + phone → Add.
              if (method === "Postage" && cartBatch) {
                this.postageLocked = true;
                this.fulfillmentMethod = "Postage";
                this.postageBatch = cartBatch;
                this.postageAck = true; // already acknowledged on the crisps page
                this.fulfillmentPrefilled = true;
                this.updateStepIndicatorText();
                const step5 = this.querySelector('[data-step-content="5"]');
                if (step5 && !step5.querySelector("[data-postage-locked-notice]")) {
                  const title = (this.config?.productTitle || "This item").replace(/</g, "&lt;");
                  const n = document.createElement("div");
                  n.setAttribute("data-postage-locked-notice", "");
                  n.className = "ys-postage-switch-notice";
                  n.setAttribute("role", "alert");
                  n.innerHTML =
                    `<strong>Heads up — your cart is set to Postage</strong> (Egg Yolk Chicken Floss Mochi Crisps only, ${cartBatch.replace(/</g, "&lt;")}). ` +
                    `${title} can't be posted, and postage can't be combined with Delivery or Self-Pickup. ` +
                    `You can add it now, but you won't be able to check out until you remove it — ` +
                    `or go back to the crisps and choose Delivery / Self-Pickup for the whole order.`;
                  step5.prepend(n);
                }
              }
              return;
            }
            selectMethod(mCard);
            if (method === "Delivery") {
              const tCard = Array.from(typeCards).find((c) => c.dataset.type === dtype);
              if (tCard) selectType(tCard);
            } else {
              const oCard = Array.from(outletCards).find((c) => c.dataset.outlet === outlet);
              if (oCard) selectOutlet(oCard);
            }
            this.fulfillmentPrefilled = true;
          })
          .catch(() => {});
      }

      // ─────────────────────────────────────────────────────────────────────────
      // MYT Timezone Utilities
      // ─────────────────────────────────────────────────────────────────────────

      /**
       * Returns the current time in Malaysia Time (UTC+8, no DST),
       * regardless of the browser's timezone setting.
       */
      getBakeryNow() {
        const utcMs = Date.now() + new Date().getTimezoneOffset() * 60000;
        return new Date(utcMs + 8 * 3600000);
      }

      /**
       * Returns true if the current MYT time is at or after the configured cutoff hour.
       * Cutoff hour comes from theme settings (default 18 = 6PM).
       */
      isAfterCutoff() {
        const cutoffHour = this.config?.cutoffHour ?? 18;
        return this.getBakeryNow().getHours() >= cutoffHour;
      }

      /**
       * Returns the earliest selectable delivery date (midnight MYT) based on:
       *   - min_preorder_days for this product (clamped to >= 0)
       *   - +1 extra day if the current MYT time is at or after the cutoff hour
       */
      getEffectiveMinDate() {
        const minDays = Math.max(0, this.config?.minPreorderDays ?? 1);
        const now = this.getBakeryNow();
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        const effectiveMinDays = minDays + (this.isAfterCutoff() ? 1 : 0);
        const minDate = new Date(today);
        minDate.setDate(today.getDate() + effectiveMinDays);
        return minDate;
      }

      /**
       * Returns a Set of blocked date strings (toDateString format) combining:
       *   - Per-product unavailable_delivery_dates metafield
       *   - Global store closure dates from theme settings (global_closed_dates)
       */
      getBlockedDateStrings() {
        const perProduct = this.config?.unavailableDates || [];
        const global = (this.config?.globalClosedDates || [])
          .map((s) => (typeof s === "string" ? s.trim() : ""))
          .filter(Boolean);
        return [...perProduct, ...global].map((d) => {
          const u = new Date(d);
          u.setHours(0, 0, 0, 0);
          return u.toDateString();
        });
      }

      // ─────────────────────────────────────────────────────────────────────────
      // Cart Prefill + Mode Detection
      // ─────────────────────────────────────────────────────────────────────────

      /**
       * Reads cart.attributes (order-level) for the stored delivery date/time/phone.
       * Sets this.mode to FRESH / CONFIRM / CONFLICT based on whether the cart date
       * is valid for this product's min_preorder_days and the current cutoff.
       *
       * FRESH    - No date in cart attributes → show full date/time/details flow
       * CONFIRM  - Cart date is valid for this product → skip date+time steps
       * CONFLICT - Cart date exists but is too early → force new date selection
       */
      /**
       * Postage is only for this product. If the cart already holds other
       * items, show a friendly notice naming them and stop. Returns true if
       * blocked. Shopify's shipping profile is the hard guard at checkout;
       * this is the explanation up front.
       */
      async checkPostageCartConflict() {
        try {
          const cart = await fetch("/cart.js").then((r) => r.json());
          const myPid = this.config?.productId;
          const others = (cart.items || []).filter((it) => it.product_id !== myPid);
          const box = this.querySelector("[data-postage-conflict]");
          if (!others.length) { if (box) box.style.display = "none"; return false; }
          const names = [...new Set(others.map((it) => it.product_title))];
          if (box) {
            box.style.display = "";
            box.innerHTML =
              `<strong>Postage is only available for Egg Yolk Chicken Floss Mochi Crisps.</strong><br>` +
              `These items in your cart can't be posted: <em>${names.map((n) => n.replace(/</g, "&lt;")).join(", ")}</em>.<br>` +
              `Please remove them to continue with postage, or choose Delivery / Self-Pickup for the whole order.`;
            box.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          return true;
        } catch (e) { return false; }
      }

      async prefillFromCart() {
        let status = { applied: false, phone: "" };

        if (!this.config?.timeSlots?.length) {
          this.mode = "FRESH";
          return status;
        }

        try {
          const res = await fetch("/cart.js");
          const cart = await res.json();

          const cartDate = cart?.attributes?.["Delivery Date"];
          const cartTime = cart?.attributes?.["Delivery Time"];
          const cartPhone = (cart?.attributes?.["Phone Number"] || "").trim();

          if (!cartDate) {
            this.mode = "FRESH";
            return status;
          }

          const parsed = this.parseStoredDeliveryDate(cartDate);
          if (!parsed) {
            this.mode = "FRESH";
            return status;
          }

          if (this.isDateValidForProduct(parsed)) {
            // CONFIRM: cart has a valid delivery date for this product
            this.mode = "CONFIRM";
            this.selectedDate = parsed;

            const timeSlots = this.config.timeSlots || [];
            const timeMatch =
              cartTime &&
              timeSlots.some(
                (s) => String(s).trim() === String(cartTime).trim()
              );
            this.selectedTime = timeMatch ? String(cartTime).trim() : null;

            status = {
              applied: true,
              mode: "CONFIRM",
              phone: cartPhone.length >= 10 ? cartPhone : "",
            };
          } else {
            // CONFLICT: cart date exists but is too early for this product
            this.mode = "CONFLICT";
            this.selectedDate = null;
            this.selectedTime = null;
            status = {
              applied: false,
              mode: "CONFLICT",
              phone: cartPhone.length >= 10 ? cartPhone : "",
              conflictDate: cartDate,
            };
          }
        } catch (e) {
          this.mode = "FRESH";
          status.reason = "Prefill failed: " + (e?.message || e);
        }

        return status;
      }

      /**
       * Parse a stored delivery date string (e.g. "February 3, 2026") to an ISO string
       * at noon local time, to avoid date boundary issues with timezone parsing.
       */
      parseStoredDeliveryDate(dateStr) {
        if (!dateStr || typeof dateStr !== "string") return null;
        const d = new Date(dateStr.trim());
        if (Number.isNaN(d.getTime())) return null;
        d.setHours(12, 0, 0, 0);
        return d.toISOString();
      }

      /**
       * Check if a date (ISO string) is valid for this product:
       *   - Must be >= the effective min date (calculated with MYT cutoff)
       *   - Must not be in the combined blocked dates list (per-product + global)
       */
      isDateValidForProduct(isoDateStr) {
        if (!isoDateStr) return false;
        const date = new Date(isoDateStr);
        if (Number.isNaN(date.getTime())) return false;

        const minDate = this.getEffectiveMinDate();
        const dateDay = new Date(date);
        dateDay.setHours(0, 0, 0, 0);

        if (dateDay < minDate) return false;

        const blockedStrings = this.getBlockedDateStrings();
        return !blockedStrings.includes(dateDay.toDateString());
      }

      /**
       * Apply the pre-selected time slot button highlight after setupTimeSlots() runs.
       */
      applyPrefilledTimeSlot() {
        if (!this.selectedTime) return;
        const btn = this.querySelector(
          `.delivery-selector__time-slot[data-time-slot="${CSS.escape(
            this.selectedTime
          )}"]`
        );
        if (btn) btn.classList.add("selected");
      }

      /**
       * Pre-fill the phone number input with the value from cart.attributes.
       */
      applyPrefilledPhone(phone) {
        if (!phone || typeof phone !== "string") return;
        const input = this.form?.querySelector('input[name="phone"]');
        if (input) input.value = phone.trim();
      }

      setupStickyAddToCartScroll() {
        const stickyAddButton = document.querySelector(
          '.sticky-cart__form button[type="submit"]'
        );
        const mainAddButton = document.querySelector(
          '.product-form__buttons button[type="submit"]'
        );

        if (stickyAddButton) {
          // Mobile CRO: the sticky bar is a LIVE CTA for the wizard.
          //  - label tells the customer what the next step is
          //    ("Choose size" → "Pick a date" → … → "Add to cart")
          //  - when everything required is filled it submits the wizard form
          //  - otherwise it scrolls to the current step (and surfaces the
          //    validation message on the final step)
          this.stickyAddButton = stickyAddButton;
          this.stickyLabelEl = stickyAddButton.querySelector(".button-title");
          const stickyBar = stickyAddButton.closest(".product-sticky-cart");
          if (stickyBar) stickyBar.classList.add("ys-wizard-sticky");

          // The theme's "Change" link points at #variant-picker, which this
          // template doesn't render. Send it back to the Size step instead.
          const changeLink = stickyBar?.querySelector(".current-variant a");
          if (changeLink) {
            changeLink.addEventListener("click", (e) => {
              e.preventDefault();
              this.hasUserGoneBack = true;
              this.advanceToStep(1, { skipPrefillAdvance: true });
              this.scrollToCurrentStep();
            });
          }

          stickyAddButton.addEventListener("click", (e) => {
            e.preventDefault();
            if (this.sheet) {
              const missing = this.firstMissing();
              if (!missing && this.isReadyToSubmit()) {
                if (typeof this.form.requestSubmit === "function") this.form.requestSubmit();
                else this.form.dispatchEvent(new Event("submit", { cancelable: true }));
                return;
              }
              if (missing) {
                this.advanceToStep(missing.step, { skipPrefillAdvance: true, noScroll: true });
                this.scrollToGroup(missing.step);
                if (missing.step === 5) this.validateDeliverySelector();
                else this.handleErrorMessage(missing.label === "Choose outlet" ? "Please choose an outlet to collect from" : "Please " + missing.label.toLowerCase());
              }
              return;
            }
            if (this.currentStep === 5) {
              // Let the normal submit path validate (it shows the phone /
              // date errors inline) and add to cart when everything is set.
              if (this.isReadyToSubmit()) {
                if (typeof this.form.requestSubmit === "function") {
                  this.form.requestSubmit();
                } else {
                  this.form.dispatchEvent(new Event("submit", { cancelable: true }));
                }
                return;
              }
              this.scrollToCurrentStep();
              this.validateDeliverySelector();
              return;
            }
            this.scrollToCurrentStep();
          });

          const phoneInput = this.form.querySelector('[name="phone"]');
          if (phoneInput) phoneInput.addEventListener("input", () => this.refreshStickyCta());
          this.refreshStickyCta();
        }

        if (mainAddButton) {
          mainAddButton.addEventListener("click", (e) => {
            if (
              !this.selectedDate ||
              !this.selectedTime ||
              !this.selectedVariantId
            ) {
              e.preventDefault();
              this.scrollIntoView({ behavior: "smooth", block: "center" });
              this.handleErrorMessage("Please complete all delivery options");
            }
          });
        }
      }

      /** Is the phone field mandatory for the current fulfilment choice? */
      isPhoneRequired() {
        if (YS_PHONE_REQUIRED_FOR_DELIVERY) return true;
        return this.fulfillmentMethod !== "Delivery";
      }

      /** Sync the phone field's required attr/label with isPhoneRequired(). */
      syncPhoneRequirement() {
        const input = this.form.querySelector('[name="phone"]');
        if (!input) return;
        const required = this.isPhoneRequired();
        input.toggleAttribute("required", required);
        const star = this.querySelector("[data-phone-required-mark]");
        if (star) star.style.display = required ? "" : "none";
        const optional = this.querySelector("[data-phone-optional-mark]");
        if (optional) optional.style.display = required ? "none" : "";
        const hint = this.querySelector("[data-phone-hint]");
        if (hint) {
          hint.textContent = required
            ? "For delivery coordination"
            : "Optional — we'll use the number you enter at checkout";
        }
      }

      /** True when the wizard could submit right now (no side effects). */
      isReadyToSubmit() {
        if (!this.selectedVariantId) return false;
        const phone = (this.form.querySelector('[name="phone"]')?.value || "").trim();
        if (this.isPhoneRequired() && phone.length < 10) return false;
        if (phone.length > 0 && phone.length < 10) return false;
        if (this.fulfillmentMethod === "Postage") {
          return !!(this.postageBatch && this.postageAck);
        }
        if (this.fulfillmentMethod === "Self-Pickup" && !this.fulfillmentOutlet) return false;
        return !!(this.selectedDate && this.selectedTime);
      }

      /** Scroll so the current wizard step sits just under the sticky header. */
      scrollToCurrentStep() {
        const target =
          this.querySelector(`[data-step-content="${this.currentStep}"]`) || this;
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      /** Sticky-bar label = the next thing the customer has to do. */
      refreshStickyCta() {
        this.refreshSheetDone();
        if (!this.stickyAddButton) return;
        if (this.sheet) {
          const missing = this.firstMissing();
          const label = missing ? missing.label : "Add to cart";
          if (this.stickyLabelEl) this.stickyLabelEl.textContent = label;
          this.stickyAddButton.setAttribute("aria-label", label);
          this.stickyAddButton.classList.toggle("ys-sticky-ready", !missing);
          return;
        }
        const labels = {
          1: "Choose size",
          2: "Pick a date",
          3: "Pick a time",
          4: "Delivery or pickup",
          5: "Add to cart",
        };
        const label = labels[this.currentStep] || "Add to cart";
        if (this.stickyLabelEl) this.stickyLabelEl.textContent = label;
        this.stickyAddButton.setAttribute("aria-label", label);
        this.stickyAddButton.classList.toggle("ys-sticky-ready", this.currentStep === 5);
      }

      loadProductConfig() {
        const configEl = this.querySelector("[data-delivery-config]");
        if (configEl) {
          try {
            this.config = JSON.parse(configEl.textContent);
            this.selectedVariantId = this.config.variantId;
          } catch (e) {
            console.error("Failed to parse delivery config:", e);
          }
        }
      }

      // ─────────────────────────────────────────────────────────────────────────
      // Cart Submission
      // ─────────────────────────────────────────────────────────────────────────

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true")
          return false;

        this.handleErrorMessage();

        // validateDeliverySelector() re-checks date validity with current MYT time
        if (!this.validateDeliverySelector()) {
          return false;
        }

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        const spinner = this.querySelector(".loading-overlay__spinner");
        if (spinner) spinner.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData();
        formData.append("id", this.selectedVariantId);
        formData.append("quantity", 1);

        // Only item-level properties (Message on Card, Birthday Partyware).
        // Delivery Date, Time, and Phone go to cart.attributes after the add succeeds.
        const properties = this.buildLineItemProperties();
        Object.keys(properties).forEach((key) => {
          formData.append(`properties[${key}]`, properties[key]);
        });

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        // Capture phone before async fetch
        const phoneValue =
          this.form.querySelector('[name="phone"]')?.value || "";

        // Step 0: a same-day basket (from /pages/same-day-delivery) can't take
        // a pre-order cake — different schedule, fee and kitchen. Ask first.
        const sameDayGuard = fetch("/cart.js")
          .then((r) => r.json())
          .then((cart) => {
            const m = cart?.attributes?.["Fulfillment Method"] || "";
            if (!/same-day/i.test(m) || !(cart?.item_count > 0)) return true;
            if (!window.confirm("Your basket holds a same-day order. Pre-order cakes need their own order — clear the basket and continue?")) return false;
            return fetch("/cart/clear.js", { method: "POST" }).then(() => true);
          })
          .catch(() => true);

        // Step 1: Add item to cart
        sameDayGuard
          .then((go) => {
            if (!go) { this.error = false; throw new Error("__ys_sameday_abort"); }
            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => response.json())
          .then((addResponse) => {
            if (addResponse.status) {
              // Shopify returned an error (e.g. out of stock)
              publish(PUB_SUB_EVENTS.cartError, {
                source: "delivery-selector",
                productVariantId: this.selectedVariantId,
                errors: addResponse.description,
                message: addResponse.message,
              });
              this.handleErrorMessage(addResponse.description);
              this.error = true;
              return;
            }

            // Step 1b: Door-to-Door chosen — make sure the RM5 service item
            // rides along (once per order). 45969902534716 = the
            // "Door-to-Door Delivery Service" product variant.
            const D2D_VARIANT_ID = 45969902534716;
            // When the service gets added here, the sections HTML captured in
            // addResponse is already stale (it misses the RM5 line) — the
            // final render below re-fetches fresh sections in that case.
            let d2dJustAdded = false;
            const maybeAddD2d =
              this.fulfillmentMethod === "Delivery" && this.deliveryType === "Door-to-Door"
                ? fetch("/cart.js")
                    .then((r) => r.json())
                    .then((cart) =>
                      cart.items.some((it) => it.variant_id === D2D_VARIANT_ID)
                        ? null
                        : fetch("/cart/add.js", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ id: D2D_VARIANT_ID, quantity: 1 }),
                          }).then((res) => {
                            d2dJustAdded = res.ok;
                            return res;
                          })
                    )
                    .catch(() => null)
                : Promise.resolve(null);

            // Step 2: Write order-level delivery info to cart.attributes (single atomic call)
            return maybeAddD2d.then(() => fetch("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                attributes: {
                  "Delivery Date": this.fulfillmentMethod === "Postage" ? "" : this.formatDate(this.selectedDate),
                  "Delivery Time": this.fulfillmentMethod === "Postage" ? "" : this.selectedTime,
                  "Postage Batch": this.fulfillmentMethod === "Postage" ? this.postageBatch : "",
                  "Phone Number": phoneValue,
                  "Fulfillment Method": this.fulfillmentMethod || "Delivery",
                  "Delivery Type":
                    this.fulfillmentMethod === "Delivery"
                      ? (this.deliveryType === "Door-to-Door" ? "Door-to-Door (+RM5)" : "Normal (lobby/guard house)")
                      : "",
                  "Pickup Outlet": this.fulfillmentOutlet || "",
                  "Pickup Location": "", "Same-Day Postcode": "", "Same-Day Zone": "", "Same-Day No": "", "Make it Circle": "",
                },
              }),
            }))
              .then((r) => {
                // Push the choice to the cart's checkout preference so the
                // checkout page opens with Delivery/Pickup preselected.
                // No-ops gracefully when the storefront token isn't set.
                if (window.YSFulfillment) {
                  window.YSFulfillment.apply(this.fulfillmentMethod || "Delivery", this.fulfillmentOutlet || "");
                }
                return r;
              })
              .then((r) => r.json())
              .then(() => {
                if (!this.cart) {
                  window.location = window.routes.cart_url;
                  return;
                }

                if (!this.error) {
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: "delivery-selector",
                    productVariantId: this.selectedVariantId,
                  });
                }

                this.error = false;

                const reinitBanner = function () {
                  setTimeout(function () {
                    document
                      .querySelectorAll("[data-cart-delivery-banner]")
                      .forEach(function (el) {
                        if (el._deliveryValidator) {
                          el._deliveryValidator.validate();
                        } else if (window._initCartDeliveryBanner) {
                          window._initCartDeliveryBanner(el);
                        }
                      });
                  }, 150);
                };

                if (d2dJustAdded) {
                  // The RM5 service was added AFTER addResponse was captured,
                  // so its sections HTML misses the item. Re-fetch fresh
                  // section HTML so the drawer shows the full cart.
                  const ids = this.cart.getSectionsToRender().map((s) => s.id);
                  fetch(`${window.location.pathname}?sections=${ids.join(",")}`)
                    .then((r) => r.json())
                    .then((sections) => {
                      this.cart.renderContents({ ...addResponse, sections });
                    })
                    .catch(() => this.cart.renderContents(addResponse))
                    .finally(reinitBanner);
                } else {
                  // Sections from the add are current (attributes aren't part
                  // of the rendered item list) — render directly.
                  this.cart.renderContents(addResponse);
                  reinitBanner();
                }
              });
          })
          .catch((e) => {
            if (e && e.message === "__ys_sameday_abort") return;
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty")) {
              this.cart.classList.remove("is-empty");
            }
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            const spinner = this.querySelector(".loading-overlay__spinner");
            if (spinner) spinner.classList.add("hidden");
          });

        return true;
      }

      async onBuyNowHandler(evt) {
        evt.preventDefault();
        const submitEvt = new Event("submit", { cancelable: true });
        const validationPassed = this.onSubmitHandler(submitEvt);
        if (validationPassed && !this.error) {
          setTimeout(() => {
            if (!this.error) {
              window.location = "/checkout";
            }
          }, 500);
        }
      }

      /**
       * Returns item-level line item properties.
       * Order-level fields (Delivery Date, Delivery Time, Phone Number) are written
       * to cart.attributes via /cart/update.js after a successful add-to-cart.
       *
       * Hidden properties (prefixed with _ so Shopify omits them from storefront display)
       * embed per-product delivery constraints so cart-delivery-validator.js can enforce
       * the correct minimum date, unavailable dates, and time slots when the customer
       * uses the cart Edit picker — without requiring additional API calls.
       */
      buildLineItemProperties() {
        const properties = {
          "Message on Card":
            this.form.querySelector('[name="message"]')?.value || "",
          // Hidden delivery constraints (read by cart-delivery-validator.js)
          _min_preorder_days: String(
            Math.max(0, this.config?.minPreorderDays ?? 1)
          ),
          _unavailable_dates: (this.config?.unavailableDates || []).join(","),
          _time_slots: (this.config?.timeSlots || []).join("|"),
        };

        if (this.config?.showPartyware) {
          properties["Birthday Partyware"] = this.partywareChoice;
        }

        return properties;
      }

      /**
       * Validates all required fields before submission.
       *
       * Critically: re-checks this.selectedDate against the CURRENT MYT cutoff,
       * catching the case where 6PM passed while the customer was filling the form.
       * If the date is now stale, routes the user back to the date step.
       */
      validateDeliverySelector() {
        // Re-validate date with current bakery time (Bug 3 fix)
        if (
          this.selectedDate &&
          !this.isDateValidForProduct(this.selectedDate)
        ) {
          this.selectedDate = null;
          this.advanceToStep(2);
          this.handleErrorMessage(
            "Your delivery date is no longer available — the order cutoff has passed. Please select a new date."
          );
          return false;
        }

        const phoneInput = this.form.querySelector('[name="phone"]');
        const phone = (phoneInput?.value || "").trim();
        // Required → must be ≥10 digits. Optional → empty is fine, but a
        // half-typed number is still rejected.
        const phoneOk = this.isPhoneRequired()
          ? phone.length >= 10
          : phone.length === 0 || phone.length >= 10;

        const isPostage = this.fulfillmentMethod === "Postage";
        const outletMissing = !isPostage && this.fulfillmentMethod === "Self-Pickup" && !this.fulfillmentOutlet;
        const isValid = isPostage
          ? (this.postageBatch && this.postageAck && this.selectedVariantId && phoneOk)
          : (this.selectedDate && this.selectedTime && this.selectedVariantId && phoneOk && !outletMissing);

        if (!isValid) {
          let errorMessage = "Please complete all delivery options";
          if (outletMissing && this.selectedDate && this.selectedTime && this.selectedVariantId) {
            errorMessage = "Please choose an outlet to collect from";
            const hint = this.querySelector("[data-pickup-hint]");
            if (hint) hint.style.display = "";
          } else if (isPostage && !this.postageBatch) {
            errorMessage = "Please choose a postage batch";
          } else if (isPostage && !this.postageAck) {
            errorMessage = "Please tick to confirm you understand how postage works";
          } else if (!isPostage && !this.selectedDate) {
            errorMessage = "Please select a delivery date";
          } else if (!isPostage && !this.selectedTime) {
            errorMessage = "Please select a delivery time";
          } else if (!this.selectedVariantId) {
            errorMessage = "Please select a size";
          } else if (!phoneOk) {
            errorMessage =
              "Please enter a valid phone number (at least 10 digits)";
            this.showPhoneValidationError(phoneInput);
          }
          this.handleErrorMessage(errorMessage);
          return false;
        }

        return true;
      }

      showPhoneValidationError(phoneInput) {
        phoneInput.classList.add("error");
        phoneInput.classList.add("shake");
        phoneInput.focus();
        setTimeout(() => {
          phoneInput.classList.remove("shake");
        }, 500);
        const removeError = () => {
          phoneInput.classList.remove("error");
          phoneInput.removeEventListener("input", removeError);
        };
        phoneInput.addEventListener("input", removeError);
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".delivery-selector__error-message-wrapper");
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".delivery-selector__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      setupStepNavigation() {
        const stepButtons = this.querySelectorAll(
          ".delivery-selector__step-item"
        );
        stepButtons.forEach((button) => {
          button.addEventListener("click", () => {
            const stepNum = parseInt(button.dataset.step);
            if (this.sheet) {
              this.advanceToStep(stepNum, { skipPrefillAdvance: true, noScroll: true });
              this.scrollToGroup(stepNum);
              return;
            }
            if (
              button.classList.contains("completed") ||
              stepNum === this.currentStep
            ) {
              this.advanceToStep(stepNum, { skipPrefillAdvance: true });
            }
          });
        });
      }

      // ─────────────────────────────────────────────────────────────────────────
      // Date Picker
      // ─────────────────────────────────────────────────────────────────────────

      setupDatePicker() {
        const datesWrapper = this.querySelector("[data-dates-wrapper]");
        const monthLabel = this.querySelector("[data-month-label]");
        const prevBtn = this.querySelector(
          ".delivery-selector__date-nav--prev"
        );
        const nextBtn = this.querySelector(
          ".delivery-selector__date-nav--next"
        );
        const calendarButton = this.querySelector("[data-calendar-trigger]");
        const calendarText = this.querySelector("[data-calendar-text]");

        if (!datesWrapper) return;

        // In CONFLICT mode, inject a warning banner below the date picker row
        // so the customer understands why they must pick a new date for the whole order.
        if (this.mode === "CONFLICT") {
          const minDate = this.getEffectiveMinDate();
          const minDateFormatted = minDate.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          });
          const warning = document.createElement("div");
          warning.className = "delivery-selector__conflict-warning";
          warning.innerHTML = `
            <span>Available from <strong>${minDateFormatted}</strong> — selecting a date will reschedule your entire order.</span>
          `;
          // Insert after the date-picker-row (dates-container → date-picker-row → after)
          const datePickerRow = datesWrapper.parentNode.parentNode;
          datePickerRow.after(warning);
        }

        // Snapshot blocked dates once per setup (re-calculated on each isDateValidForProduct call)
        const blockedDateStrings = this.getBlockedDateStrings();

        let currentOffset = 0;
        const datesPerPage = 3;

        const generateDates = (offset = 0) => {
          const dates = [];
          const minDate = this.getEffectiveMinDate();
          const today = this.getBakeryNow();
          today.setHours(0, 0, 0, 0);
          // Use integer arithmetic to avoid DST edge cases (MYT has no DST, extra safety)
          const effectiveMinDays = Math.round(
            (minDate - today) / (1000 * 60 * 60 * 24)
          );

          let datesAdded = 0;
          let dayCounter = 0;

          while (datesAdded < datesPerPage && dayCounter < 90) {
            const date = new Date(today);
            date.setDate(
              today.getDate() + effectiveMinDays + dayCounter + offset
            );

            if (!blockedDateStrings.includes(date.toDateString())) {
              dates.push(date);
              datesAdded++;
            }

            dayCounter++;
          }

          return dates;
        };

        // If prefilled date exists, position the carousel page to show it
        if (this.selectedDate) {
          const targetDateString = new Date(this.selectedDate).toDateString();
          for (let off = 0; off < 90; off++) {
            const dates = generateDates(off);
            if (dates.some((d) => d.toDateString() === targetDateString)) {
              currentOffset = off;
              break;
            }
          }
        }

        const renderDates = () => {
          const dates = generateDates(currentOffset);

          // Handle edge case: all dates blocked within 90-day window
          if (dates.length === 0) {
            datesWrapper.innerHTML = `
              <p class="delivery-selector__no-dates">
                No delivery dates are currently available for this product.
                Please contact us for assistance.
              </p>
            `;
            if (prevBtn) prevBtn.disabled = true;
            if (nextBtn) nextBtn.disabled = true;
            return;
          }

          if (monthLabel) {
            monthLabel.textContent = dates[0].toLocaleDateString("en-US", {
              month: "long",
            });
          }

          datesWrapper.innerHTML = dates
            .map((date) => {
              const isSelected =
                this.selectedDate &&
                new Date(this.selectedDate).toDateString() ===
                  date.toDateString();
              return `
                <button
                  type="button"
                  class="delivery-selector__date ${
                    isSelected ? "selected" : ""
                  }"
                  data-date="${date.toISOString()}"
                >
                  <span class="delivery-selector__date-day">${date.getDate()}</span>
                  <span class="delivery-selector__date-weekday">${date
                    .toLocaleDateString("en-US", { weekday: "short" })
                    .toUpperCase()}</span>
                </button>
              `;
            })
            .join("");

          // Bug 2 fix: re-validate at click time, not just at render time.
          // 6PM may have passed since this button was rendered.
          datesWrapper
            .querySelectorAll(".delivery-selector__date")
            .forEach((btn) => {
              btn.addEventListener("click", () => {
                if (!this.isDateValidForProduct(btn.dataset.date)) {
                  // Cutoff just passed — re-render picker with updated min date
                  renderDates();
                  this.handleErrorMessage(
                    "That date is no longer available — the order cutoff has passed. Please select another date."
                  );
                  return;
                }
                this.handleErrorMessage(); // clear any previous error
                this.selectedDate = btn.dataset.date;
                renderDates();
                this.updateStepIndicatorText();
                this.advanceToStep(3);
              });
            });

          if (prevBtn) prevBtn.disabled = currentOffset === 0;
        };

        if (prevBtn) {
          prevBtn.addEventListener("click", () => {
            if (currentOffset > 0) {
              currentOffset -= datesPerPage;
              renderDates();
            }
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener("click", () => {
            currentOffset += datesPerPage;
            renderDates();
          });
        }

        renderDates();

        // ── Native calendar picker integration ──
        const calendarTrigger = this.querySelector("[data-calendar-trigger]");
        const nativeDatePicker = this.querySelector(
          "[data-native-date-picker]"
        );

        if (calendarTrigger && nativeDatePicker) {
          const updatePickerMinMax = () => {
            const minDate = this.getEffectiveMinDate();
            const maxDate = new Date(minDate);
            maxDate.setDate(minDate.getDate() + 60);
            nativeDatePicker.min = minDate.toISOString().split("T")[0];
            nativeDatePicker.max = maxDate.toISOString().split("T")[0];
          };

          // Set initial min/max
          updatePickerMinMax();

          calendarTrigger.addEventListener("click", () => {
            // Refresh min/max in case cutoff just passed while page was open
            updatePickerMinMax();
            nativeDatePicker.showPicker
              ? nativeDatePicker.showPicker()
              : nativeDatePicker.click();
          });

          const resetCalendarButton = () => {
            if (calendarButton && calendarText) {
              calendarButton.classList.remove("error");
              calendarText.textContent = "Show Full Calendar";
            }
          };

          const showCalendarError = (dateStr) => {
            if (calendarButton && calendarText) {
              calendarButton.classList.add("error");
              calendarText.textContent = `${dateStr} is unavailable, please choose another date`;
              setTimeout(resetCalendarButton, 5000);
            }
          };

          nativeDatePicker.addEventListener("change", () => {
            const selectedDateStr = nativeDatePicker.value;
            if (!selectedDateStr) return;

            const selected = new Date(selectedDateStr + "T00:00:00");
            const selectedISO = selected.toISOString();

            if (!this.isDateValidForProduct(selectedISO)) {
              const minDate = this.getEffectiveMinDate();
              const minFormatted = minDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              });
              if (blockedDateStrings.includes(selected.toDateString())) {
                showCalendarError(
                  selected.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                );
              } else {
                this.handleErrorMessage(
                  `Please select a date from ${minFormatted} or later.`
                );
              }
              nativeDatePicker.value = "";
              return;
            }

            resetCalendarButton();
            this.handleErrorMessage();
            this.selectedDate = selectedISO;
            renderDates();
            this.updateStepIndicatorText();
            this.advanceToStep(3);
          });
        }
      }

      setupTimeSlots() {
        const timeSlots = this.querySelectorAll(
          ".delivery-selector__time-slot"
        );

        timeSlots.forEach((slot) => {
          slot.addEventListener("click", () => {
            timeSlots.forEach((s) => s.classList.remove("selected"));
            slot.classList.add("selected");
            this.selectedTime = slot.dataset.timeSlot;
            this.updateStepIndicatorText();
            this.advanceToStep(this.config?.receiveFirst ? 5 : 4);
            this.updateSummary();
          });
        });
      }

      setupOptions() {
        const sizeOptions = this.querySelectorAll(
          ".delivery-selector__size-option"
        );

        const selectSize = (option) => {
          sizeOptions.forEach((o) => o.classList.remove("selected"));
          option.classList.add("selected");

          this.selectedVariantId = option.dataset.variantId;
          this.selectedVariantPrice = option.dataset.variantPrice;
          this.selectedVariantTitle = option.dataset.variantTitle;

          const hiddenInput = this.form.querySelector('[name="id"]');
          if (hiddenInput) hiddenInput.value = this.selectedVariantId;

          this.updateStepIndicatorText();
          this.updatePriceDisplay();
        };

        // Leaving the Size step — same routing the "Next" button uses.
        const goNextFromSize = () => {
          if (!this.selectedVariantId) {
            this.handleErrorMessage("Please select a size");
            return;
          }
          if (this.postageLocked) { this.advanceToStep(5); this.updateSummary(); return; }
          this.advanceToStep(this.config?.receiveFirst ? 4 : 2);
        };
        this.goNextFromSize = goNextFromSize;

        sizeOptions.forEach((option) => {
          option.addEventListener("click", () => {
            if (option.disabled) return;
            selectSize(option);
            // Mobile CRO: tapping a size moves on immediately (Date and Time
            // steps already auto-advance) — saves the extra "Next" tap.
            goNextFromSize();
          });
        });

        // Mobile CRO: single-variant products (daifuku, minis, rolls) have
        // nothing to choose here — pre-select the only option and start on
        // the Date step instead of showing a one-button "Customize" screen.
        if (
          sizeOptions.length === 1 &&
          !sizeOptions[0].disabled &&
          this.currentStep === 1 &&
          !this.hasUserGoneBack
        ) {
          selectSize(sizeOptions[0]);
          goNextFromSize();
        }

        const partywareOptions = this.querySelectorAll(
          ".delivery-selector__partyware-option"
        );

        partywareOptions.forEach((option) => {
          option.addEventListener("click", () => {
            partywareOptions.forEach((o) => o.classList.remove("selected"));
            option.classList.add("selected");
            this.partywareChoice = option.dataset.partyware;
            const hiddenInput = this.form.querySelector('[name="partyware"]');
            if (hiddenInput) hiddenInput.value = this.partywareChoice;
          });
        });

        const nextBtn = this.querySelector("[data-next-step]");
        if (nextBtn) {
          nextBtn.addEventListener("click", goNextFromSize);
        }

        // Sheet: the engine already defaults selectedVariantId to the first
        // available variant (hidden input + price). Show that honestly by
        // highlighting the matching chip, so "done" on group 1 is visible
        // and the customer can change it with one tap.
        if (this.sheet && this.selectedVariantId && !this.querySelector(".delivery-selector__size-option.selected")) {
          const match = [...sizeOptions].find((o) => o.dataset.variantId === String(this.selectedVariantId) && !o.disabled);
          if (match) {
            match.classList.add("selected");
            this.selectedVariantPrice = match.dataset.variantPrice;
            this.selectedVariantTitle = match.dataset.variantTitle;
            this.updateStepIndicatorText();
            this.refreshStickyCta();
          }
        }
      }

      setupFinalDetails() {
        const messageTextarea = this.form.querySelector('[name="message"]');
        const charCount = this.querySelector("[data-char-count]");

        if (messageTextarea && charCount) {
          messageTextarea.addEventListener("input", () => {
            charCount.textContent = messageTextarea.value.length;
          });
        }

        const editBtn = this.querySelector("[data-edit-selections]");
        if (editBtn) {
          editBtn.addEventListener("click", () => {
            this.hasUserGoneBack = true;
            this.advanceToStep(1, { skipPrefillAdvance: true });
          });
        }
      }

      advanceToStep(stepNumber, options = {}) {
        this.currentStep = stepNumber;

        const stepIndicators = this.querySelectorAll(
          ".delivery-selector__step-item"
        );
        stepIndicators.forEach((indicator) => {
          const stepNum = parseInt(indicator.dataset.step);
          indicator.classList.remove("active", "completed");

          if (stepNum < stepNumber) {
            indicator.classList.add("completed");
            indicator.removeAttribute("disabled");
          } else if (stepNum === stepNumber) {
            indicator.classList.add("active");
            indicator.removeAttribute("disabled");
          } else if (this.sheet) {
            indicator.removeAttribute("disabled"); // every group is reachable on the sheet
          } else {
            indicator.setAttribute("disabled", "disabled");
          }
        });

        this.updateStepIndicatorText();

        const stepContents = this.querySelectorAll("[data-step-content]");
        if (this.sheet) {
          // Sheet: everything stays visible; only the highlight moves.
          // (Postage-locked carts still hide Date/Time/Receive — they don't apply.)
          stepContents.forEach((content) => {
            const step = parseInt(content.dataset.stepContent);
            const hidden = this.postageLocked && step >= 2 && step <= 4;
            content.style.display = hidden ? "none" : "block";
            content.classList.toggle("is-active", step === stepNumber);
          });
          if (this.sheetReady && !options.noScroll) {
            // Bring the next group into view only if it's below the fold.
            const el = this.querySelector(`[data-step-content="${stepNumber}"]`);
            if (el) {
              const r = el.getBoundingClientRect();
              if (r.top > window.innerHeight * 0.6 || r.top < 60) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }
          }
        } else {
          stepContents.forEach((content) => {
            const step = parseInt(content.dataset.stepContent);
            content.style.display = step === stepNumber ? "block" : "none";
          });
        }

        if (stepNumber === 5 || this.sheet) this.syncPhoneRequirement();
        this.refreshStickyCta();

        if (stepNumber === 5 && !this.sheet) {
          // Auto-focus the phone field on desktop only. On phones a programmatic
          // focus pops the keyboard over half the viewport (and the sticky CTA),
          // and on iOS it scroll-jumps the page. Let the customer tap it.
          const isTouch = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
          if (!isTouch) {
            setTimeout(() => {
              const phoneInput = this.querySelector(
                'input[type="tel"][data-auto-focus]'
              );
              if (phoneInput) phoneInput.focus();
            }, 100);
          }
        }

        // Auto-advance when Date/Time are pre-set (CONFIRM mode or prefill from cart).
        // Skipped when user manually navigated back via "Edit" or step indicators.
        if (this.postageLocked && stepNumber >= 2 && stepNumber <= 4) {
          // Postage-locked cart: Date/Time/Receive don't apply — go to details.
          this.advanceToStep(5, options);
          this.updateSummary();
          return;
        }
        if (!options.skipPrefillAdvance && !this.hasUserGoneBack) {
          if (stepNumber === 2 && this.selectedDate) {
            this.advanceToStep(3);
          } else if (stepNumber === 3 && this.selectedTime) {
            this.advanceToStep(this.config?.receiveFirst ? 5 : 4);
            this.updateSummary();
          } else if (stepNumber === 4 && this.fulfillmentPrefilled) {
            // Receive method already chosen earlier this order — skip ahead.
            if (this.config?.receiveFirst && this.fulfillmentMethod !== "Postage" && !this.selectedDate) {
              this.advanceToStep(2);
            } else {
              this.advanceToStep(5);
            }
            this.updateSummary();
          }
        }
      }

      updateStepIndicatorText() {
        // Postage skips Date/Time — grey them out and label accordingly.
        const dtIndicators = this.querySelectorAll('.delivery-selector__step-item[data-step="2"], .delivery-selector__step-item[data-step="3"]');
        dtIndicators.forEach((el) => el.classList.toggle("is-postage-hidden", this.fulfillmentMethod === "Postage"));
        const sizeIndicator = this.querySelector("[data-size-text]");
        if (sizeIndicator && this.selectedVariantTitle) {
          sizeIndicator.textContent = this.selectedVariantTitle;
        }

        const dateIndicator = this.querySelector(
          '.delivery-selector__step-item[data-step="2"] .delivery-selector__step-text'
        );
        if (dateIndicator && this.selectedDate) {
          const date = new Date(this.selectedDate);
          dateIndicator.textContent = date.toLocaleDateString("en-US", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        }

        const timeIndicator = this.querySelector(
          '.delivery-selector__step-item[data-step="3"] .delivery-selector__step-text'
        );
        if (timeIndicator && this.selectedTime) {
          timeIndicator.textContent = this.selectedTime;
        }
      }

      updateSummary() {
        const summaryDate = this.querySelector("[data-summary-date]");
        const summaryTime = this.querySelector("[data-summary-time]");
        const summarySize = this.querySelector("[data-summary-size]");
        const summaryPartyware = this.querySelector("[data-summary-partyware]");
        const summaryPartywareContainer = this.querySelector(
          "[data-summary-partyware-container]"
        );

        const isPostageSummary = this.fulfillmentMethod === "Postage";
        if (summaryDate)
          summaryDate.textContent = isPostageSummary ? "Ships within batch window" : this.formatDate(this.selectedDate);
        if (summaryTime) summaryTime.textContent = isPostageSummary ? "Tracking link sent when posted" : this.selectedTime;
        if (summarySize)
          summarySize.textContent = `${this.selectedVariantTitle} (${this.selectedVariantPrice})`;

        const summaryReceive = this.querySelector("[data-summary-receive]");
        if (summaryReceive) {
          summaryReceive.textContent =
            this.fulfillmentMethod === "Postage"
              ? `📦 Postage — ${this.postageBatch || "batch"} · arrives 2–3 working days after posting`
              : this.fulfillmentMethod === "Self-Pickup"
                ? `🏬 Pickup @ ${this.fulfillmentOutlet || "outlet"}`
                : this.deliveryType === "Door-to-Door"
                  ? "🛵 Door-to-Door Delivery (+RM5)"
                  : "🛵 Normal Delivery (lobby/guard house)";
        }

        if (this.config?.showPartyware) {
          if (summaryPartyware)
            summaryPartyware.textContent = this.partywareChoice;
          if (summaryPartywareContainer)
            summaryPartywareContainer.style.display = "";
        } else {
          if (summaryPartywareContainer)
            summaryPartywareContainer.style.display = "none";
        }
      }

      formatDate(dateString) {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }

      // ─────────────────────────────────────────────────────────────────────────
      // Price Display
      // ─────────────────────────────────────────────────────────────────────────

      setupPriceUpdate() {
        const priceElement = document.querySelector(".regular-price");
        if (priceElement) {
          const initialPriceText = priceElement.textContent.trim();
          this.currencyFormat = this.detectCurrencyFormat(initialPriceText);

          if (this.config?.variants && this.config.variants.length > 0) {
            const firstVariant = this.config.variants.find(
              (v) => v.id === this.config.variantId
            );
            if (firstVariant) {
              this.selectedVariantPrice = (firstVariant.price / 100).toString();
              this.updatePriceDisplay();
            }
          }
        }

        const quantityInputs = document.querySelectorAll(
          'quantity-input input[type="number"][name="quantity"]'
        );
        quantityInputs.forEach((input) => {
          input.addEventListener("change", () => this.updatePriceDisplay());
          input.addEventListener("input", () => this.updatePriceDisplay());
        });
      }

      detectCurrencyFormat(priceText) {
        const match = priceText.match(/^([^\d]*)([\d,]+\.?\d*)(.*)$/);
        if (match) {
          return {
            prefix: match[1] || "",
            suffix: match[3] || "",
            hasCommas: priceText.includes(","),
          };
        }
        return { prefix: "", suffix: "", hasCommas: false };
      }

      formatPrice(price) {
        if (!this.currencyFormat) {
          return new Intl.NumberFormat("en-MY", {
            style: "currency",
            currency: "MYR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(price);
        }

        let formattedNumber = price.toFixed(2);
        if (this.currencyFormat.hasCommas) {
          formattedNumber = parseFloat(formattedNumber).toLocaleString(
            "en-US",
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          );
        }

        return `${this.currencyFormat.prefix}${formattedNumber}${this.currencyFormat.suffix}`;
      }

      updatePriceDisplay() {
        const quantityInput = document.querySelector(
          'quantity-input input[type="number"][name="quantity"]'
        );
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

        let unitPrice = null;

        if (this.selectedVariantPrice) {
          unitPrice = parseFloat(this.selectedVariantPrice);
        } else if (this.config?.variants && this.config.variants.length > 0) {
          const firstVariant = this.config.variants.find(
            (v) => v.id === this.config.variantId
          );
          if (firstVariant) {
            unitPrice = firstVariant.price / 100;
          } else {
            return;
          }
        } else {
          return;
        }

        const totalPrice = unitPrice * quantity;

        const priceElements = document.querySelectorAll(".regular-price");
        priceElements.forEach((element) => {
          if (element.closest("s")) return;
          element.textContent = this.formatPrice(totalPrice);
        });

        const salePriceElements = document.querySelectorAll(".sale-price");
        salePriceElements.forEach((element) => {
          element.textContent = this.formatPrice(totalPrice);
        });
      }
    }
  );
}
